import { generateSimpleHash } from "./hash";

const toFloat = (value) => {
  if (typeof value === "number") return value;
  return parseFloat(value);
};

const replaceNaN = (value, replacement) => {
  return typeof value === "number" && isNaN(value) ? replacement : value;
};

const toInt = (value) => {
  if (typeof value === "number") return value;
  return parseInt(value);
};

const getNavigatorValues = () => {
  return navigator;
};

const getPlugins = () => {
  const rawPlugins = navigator.plugins;

  if (!rawPlugins) {
    return undefined;
  }

  const plugins = [];

  for (let i = 0; i < rawPlugins.length; ++i) {
    const plugin = rawPlugins[i];
    if (!plugin) {
      continue;
    }

    const mimeTypes = [];
    for (let j = 0; j < plugin.length; ++j) {
      const mimeType = plugin[j];
      if (!mimeType) {
        continue;
      }
      mimeTypes.push({
        type: mimeType.type,
        suffixes: mimeType.suffixes,
      });
    }

    plugins.push({
      name: plugin.name,
      description: plugin.description,
      mimeTypes,
    });
  }

  return JSON.stringify(plugins);
};

const getScreenResolution = () => {
  const s = screen;
  const parseDimension = (value) => replaceNaN(toInt(value), null);
  const dimensions = [parseDimension(s.width), parseDimension(s.height)];
  dimensions.sort().reverse();
  return dimensions;
};

const getTouchSupport = () => {
  const n = navigator;

  let maxTouchPoints = 0;
  let touchEvent;
  if (n.maxTouchPoints !== undefined) {
    maxTouchPoints = toInt(n.maxTouchPoints);
  } else if (n.msMaxTouchPoints !== undefined) {
    maxTouchPoints = n.msMaxTouchPoints;
  }
  try {
    document.createEvent("TouchEvent");
    touchEvent = true;
  } catch {
    touchEvent = false;
  }
  const touchStart = "ontouchstart" in window;
  return {
    maxTouchPoints,
    touchEvent,
    touchStart,
  };
};

const getColorDepth = () => {
  return window.screen.colorDepth;
};

const getMathFingerprint = () => {
  const M = Math;
  const fallbackFn = () => 0;
  const acos = M.acos || fallbackFn;
  const acosh = M.acosh || fallbackFn;
  const asin = M.asin || fallbackFn;
  const asinh = M.asinh || fallbackFn;
  const atanh = M.atanh || fallbackFn;
  const atan = M.atan || fallbackFn;
  const sin = M.sin || fallbackFn;
  const sinh = M.sinh || fallbackFn;
  const cos = M.cos || fallbackFn;
  const cosh = M.cosh || fallbackFn;
  const tan = M.tan || fallbackFn;
  const tanh = M.tanh || fallbackFn;
  const exp = M.exp || fallbackFn;
  const expm1 = M.expm1 || fallbackFn;
  const log1p = M.log1p || fallbackFn;

  const powPI = (value) => M.pow(M.PI, value);
  const acoshPf = (value) => M.log(value + M.sqrt(value * value - 1));
  const asinhPf = (value) => M.log(value + M.sqrt(value * value + 1));
  const atanhPf = (value) => M.log((1 + value) / (1 - value)) / 2;
  const sinhPf = (value) => M.exp(value) - 1 / M.exp(value) / 2;
  const coshPf = (value) => (M.exp(value) + 1 / M.exp(value)) / 2;
  const expm1Pf = (value) => M.exp(value) - 1;
  const tanhPf = (value) => (M.exp(2 * value) - 1) / (M.exp(2 * value) + 1);
  const log1pPf = (value) => M.log(1 + value);

  return {
    acos: acos(0.123124234234234242),
    acosh: acosh(1e308),
    acoshPf: acoshPf(1e154),
    asin: asin(0.123124234234234242),
    asinh: asinh(1),
    asinhPf: asinhPf(1),
    atanh: atanh(0.5),
    atanhPf: atanhPf(0.5),
    atan: atan(0.5),
    sin: sin(-1e300),
    sinh: sinh(1),
    sinhPf: sinhPf(1),
    cos: cos(10.000000000123),
    cosh: cosh(1),
    coshPf: coshPf(1),
    tan: tan(-1e300),
    tanh: tanh(1),
    tanhPf: tanhPf(1),
    exp: exp(1),
    expm1: expm1(1),
    expm1Pf: expm1Pf(1),
    log1p: log1p(10),
    log1pPf: log1pPf(10),
    powPI: powPI(-100),
  };
};

const getVideoCardInfo = () => {
  const gl = document.createElement("canvas").getContext("webgl");
  if (!gl)
    return {
      error: "No webgl",
    };

  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  return debugInfo
    ? {
        vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
        renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
      }
    : {
        error: "No WEBGL_debug_renderer_info",
      };
};

const getTimezoneOffset = () => {
  const currentYear = new Date().getFullYear();

  return Math.max(
    toFloat(new Date(currentYear, 0, 1).getTimezoneOffset()),
    toFloat(new Date(currentYear, 6, 1).getTimezoneOffset())
  );
};

const getTimezone = () => {
  const DateTimeFormat = window?.Intl?.DateTimeFormat;

  if (DateTimeFormat) {
    const timezone = new DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      return timezone;
    }
  }

  const offset = -getTimezoneOffset();
  return `UTC${offset >= 0 ? "+" : ""}${Math.abs(offset)}`;
};

const getUserAgent = () => {
  return navigator.userAgent;
};

export const useDeviceData = () => {
  const getFingerprint = () => {
    return {
      timezone: getTimezone(),
      videoCardInfo: getVideoCardInfo(),
      mathFingerprint: getMathFingerprint(),
      colorDepth: getColorDepth(),
      touchSupport: getTouchSupport(),
      screenResolution: getScreenResolution(),
      plugins: getPlugins(),
      navigator: getNavigatorValues(),
      userAgent: getUserAgent(),
    };
  };

  const getFingerprintHash = () => {
    return generateSimpleHash(JSON.stringify(getFingerprint()));
  };

  return {
    getFingerprint,
    getFingerprintHash,
  };
};
